import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthFlowService } from '../auth/auth-flow.service';
import { HandoffLoopBreaker } from '../handoff-loop-breaker';
import { LandingResolver } from '../landing-resolver.service';
import { PortalNavigator } from '../portal-navigator.service';
import { ShellRoutes } from '../portal-routes';

/**
 * The landing route is never displayed: it only redirects to the resolved target.
 * Authentication is checked here and not by a sibling guard, because Angular starts the guards of a route
 * concurrently and the deeplink must not be consumed before the user is signed in.
 */
export const landingGuard: CanActivateFn = async (_route, state) => {
  const authFlow = inject(AuthFlowService);
  const resolver = inject(LandingResolver);
  const navigator = inject(PortalNavigator);
  // Injected up front: the injection context does not survive the first `await`
  const loopBreaker = inject(HandoffLoopBreaker);
  const router = inject(Router);

  const signedIn = await authFlow.ensureSignedIn(state.url);
  if (signedIn !== true) {
    return signedIn;
  }

  const target = await resolver.resolve();
  if (target.kind === 'external' && !loopBreaker.allowHandoff()) {
    // Legacy keeps sending the user back: stop the loop instead of handing over again
    return router.parseUrl(ShellRoutes.error);
  }
  return navigator.toGuardResult(target);
};
